import { AdapterType, ProviderConfig } from "../../core/types";
import {
  getProviders,
  deleteProvider,
  getDefaultMaxContextTokens,
  isModelSpecificMaxContext,
  getSessions,
} from "../../core/storage";
import { getStrings, languageToggleHtml, wireLanguageToggleButtons } from "../../core/i18n";
import { escapeHtml } from "./domUtils";

// How much of a configured system prompt to show per card — enough to recognize which prompt
// it is at a glance without a multi-line card breaking the list's compact layout.
const SYSTEM_PROMPT_SNIPPET_CHARS = 80;

function formatTemperature(p: ProviderConfig): string {
  const s = getStrings();
  return s.providerList.temperature(
    p.temperature !== undefined ? String(p.temperature) : s.providerList.defaultValue
  );
}

// Mirrors providerForm.ts's own placeholder logic (getDefaultMaxContextTokens/
// isModelSpecificMaxContext) so an unset value here shows the same number — and the same
// "model default" vs generic "(default)" distinction — the form would show as a placeholder if
// you opened Edit.
function formatMaxContextTokens(p: ProviderConfig): string {
  const value = p.maxContextTokens ?? getDefaultMaxContextTokens(p, { model: p.model });
  const defaultKind: "none" | "model" | "generic" =
    p.maxContextTokens !== undefined
      ? "none"
      : isModelSpecificMaxContext(p, { model: p.model })
        ? "model"
        : "generic";
  return getStrings().providerList.maxContext(value.toLocaleString(), defaultKind);
}

function systemPromptSnippet(prompt: string): string {
  const trimmed = prompt.trim();
  return trimmed.length > SYSTEM_PROMPT_SNIPPET_CHARS
    ? `${trimmed.slice(0, SYSTEM_PROMPT_SNIPPET_CHARS)}…`
    : trimmed;
}

export interface ProviderListHandlers {
  onAdd: () => void;
  onEdit: (provider: ProviderConfig) => void;
  // Jumps straight to "Start a new chat" with this provider pre-selected (see ContentMode's own
  // comment in chat.ts) — added 2026-08-11 alongside routing a freshly-*added* provider to this
  // list instead of straight back to chat, so the natural next step ("now actually use it") is
  // one click from here instead of add-provider-then-hunt-for-it-in-the-new-chat-dropdown.
  onStartChat: (provider: ProviderConfig) => void;
  onBackToChat: (() => void) | null;
  onChanged: () => void;
}

function adapterTypeLabel(adapterType: AdapterType): string {
  const s = getStrings();
  switch (adapterType) {
    case "gemini":
      return s.providerForm.adapterGemini;
    case "anthropic":
      return s.providerForm.adapterAnthropic;
    default:
      return s.providerForm.adapterOpenAiCompatible;
  }
}

// Add/edit/delete providers.
// The actions area sits below the name (not beside it) and reserves a fixed min-height sized
// for the taller "confirm delete" state, so switching between Edit/Delete and the confirm
// row never changes the card's height — which means sibling cards in the list never shift
// either. Delete confirmation avoids window.confirm() — native modal dialogs are unreliable
// inside Office task panes generally (a long-standing Office Add-ins host constraint, not
// specific to any one rendering engine), so this renders its own inline confirm state instead.
export function renderProviderList(
  container: HTMLElement,
  handlers: ProviderListHandlers,
  confirmingDeleteId: string | null = null
): void {
  const s = getStrings();
  const providers = getProviders();

  container.innerHTML = `
    <div class="screen provider-list-screen">
      <div class="screen-header">
        <h2>${s.providerList.title}</h2>
        ${handlers.onBackToChat ? `<button type="button" class="link-btn" data-action="back">${s.common.backToChat}</button>` : ""}
      </div>
      <div class="screen-list-scroll">
        ${
          providers.length === 0
            ? `<p class="empty-hint">${s.providerList.emptyHint}</p>`
            : `<ul class="provider-items">
                ${providers
                  .map((p) => {
                    const isConfirming = confirmingDeleteId === p.id;
                    return `
                  <li class="provider-item">
                    <div class="provider-item-info">
                      <strong>${escapeHtml(p.name)}</strong>
                      <span class="provider-meta">${adapterTypeLabel(p.adapterType)} &middot; ${escapeHtml(p.model)}</span>
                      <span class="provider-meta">${formatTemperature(p)}</span>
                      <span class="provider-meta">${formatMaxContextTokens(p)}</span>
                      ${
                        p.systemPrompt && p.systemPrompt.trim()
                          ? `<span class="provider-meta provider-system-prompt">&ldquo;${escapeHtml(systemPromptSnippet(p.systemPrompt))}&rdquo;</span>`
                          : ""
                      }
                    </div>
                    <div class="provider-item-actions">
                      ${
                        isConfirming
                          ? `<p class="confirm-text">${s.providerList.deleteConfirm(getSessions().filter((sess) => sess.providerId === p.id).length)}</p>
                             <div class="actions-row">
                               <button type="button" class="danger-btn" data-action="confirm-delete" data-id="${p.id}">${s.common.yesDelete}</button>
                               <button type="button" class="secondary-btn" data-action="cancel-delete" data-id="${p.id}">${s.common.cancel}</button>
                             </div>`
                          : `<div class="actions-row">
                               <button type="button" class="primary-btn" data-action="start-chat" data-id="${p.id}">${s.providerList.startNewChat}</button>
                               <button type="button" class="secondary-btn" data-action="edit" data-id="${p.id}">${s.common.edit}</button>
                               <button type="button" class="danger-btn" data-action="delete" data-id="${p.id}">${s.common.delete}</button>
                             </div>`
                      }
                    </div>
                  </li>`;
                  })
                  .join("")}
              </ul>`
        }
      </div>
      <div class="screen-fixed-footer">
        <button type="button" class="primary-btn" id="add-provider-btn">${s.providerList.addProvider}</button>
        ${providers.length === 0 ? languageToggleHtml() : ""}
        <div class="provider-list-info">
          <p class="provider-list-info-heading">${s.providerList.infoHeading}</p>
          <p class="provider-list-info-body">${s.providerList.infoBody}</p>
        </div>
      </div>
    </div>
  `;

  // No-op when the list isn't empty (no matching elements) — languageToggleHtml() above is only
  // rendered for the empty state, which reads like initial setup (same bare "one button on an
  // empty screen" shape as onboarding.ts), so it gets its own prominent language choice rather
  // than relying on the easy-to-miss footer one. onChanged() re-renders this same screen, which
  // is enough to reflect the new language — no separate reload needed.
  wireLanguageToggleButtons(container, () => handlers.onChanged());

  container.querySelector("#add-provider-btn")?.addEventListener("click", handlers.onAdd);
  container
    .querySelector('[data-action="back"]')
    ?.addEventListener("click", () => handlers.onBackToChat?.());

  container.querySelectorAll<HTMLButtonElement>('[data-action="edit"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      const provider = providers.find((p) => p.id === btn.dataset.id);
      if (provider) handlers.onEdit(provider);
    });
  });

  container.querySelectorAll<HTMLButtonElement>('[data-action="start-chat"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      const provider = providers.find((p) => p.id === btn.dataset.id);
      if (provider) handlers.onStartChat(provider);
    });
  });

  container.querySelectorAll<HTMLButtonElement>('[data-action="delete"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      renderProviderList(container, handlers, btn.dataset.id || null);
    });
  });

  container.querySelectorAll<HTMLButtonElement>('[data-action="cancel-delete"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      renderProviderList(container, handlers, null);
    });
  });

  container.querySelectorAll<HTMLButtonElement>('[data-action="confirm-delete"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      if (btn.dataset.id) deleteProvider(btn.dataset.id);
      handlers.onChanged();
    });
  });
}
